import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Loader2, CheckCircle, FileText, Trash2, ArrowLeft, Shield } from 'lucide-react'
import toast from 'react-hot-toast'
import DocumentsRequiredModal from '../components/DocumentsRequiredModal'
import VerificationDocumentsList, {
  type VerificationDocumentItem,
} from '../components/VerificationDocumentsList'
import { ACCEPT_STRINGS, ACCEPTED_MIME_TYPES, validateDocumentFile } from '../config/mediaValidation'
import { schoolVerificationService } from '../services/schoolVerificationService'

const initialDocData = {
  businessRegNumber: '',
  businessRegFile: null as File | null,
  gstNumber: '',
  gstFile: null as File | null,
  addressProofFile: null as File | null,
  yogaAllianceNumber: '',
  yogaAllianceFile: null as File | null,
}

const UploadSchoolDocuments: React.FC = () => {
  const { schoolId } = useParams<{ schoolId: string }>()
  const navigate = useNavigate()
  const [schoolName, setSchoolName] = useState('')
  const [existingDocuments, setExistingDocuments] = useState<VerificationDocumentItem[]>([])
  const [verificationStatus, setVerificationStatus] = useState<string>('PENDING')
  const [docData, setDocData] = useState(initialDocData)
  const [otherDocuments, setOtherDocuments] = useState<File[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [loadingStatus, setLoadingStatus] = useState(true)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [showDocumentsModal, setShowDocumentsModal] = useState(false)
  const initialMetaRef = useRef<{ businessRegNumber: string; gstNumber: string; yogaAllianceNumber: string } | null>(
    null
  )

  const fileInputClass =
    'block w-full text-sm text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-amber-50 file:text-amber-800 hover:file:bg-amber-100 file:cursor-pointer border border-gray-200 rounded-xl bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all'
  const textInputClass =
    'w-full px-4 py-2.5 text-sm border border-gray-200 bg-white rounded-xl placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all'

  const existingTypesOnProfile = useMemo(
    () => new Set(existingDocuments.map((d) => d.type)),
    [existingDocuments]
  )

  const replaceHint = (type: string) =>
    existingTypesOnProfile.has(type) ? (
      <p className="text-xs text-amber-700 mt-1">A file is already on profile — upload will replace it.</p>
    ) : null

  useEffect(() => {
    if (!schoolId) return
    let mounted = true
    const load = async () => {
      try {
        const data = await schoolVerificationService.getStatus(schoolId)
        if (!mounted) return
        setSchoolName(data.schoolName || '')
        setExistingDocuments(data.documents || [])
        setVerificationStatus(data.status || 'PENDING')
        const m = data.verificationMeta || {}
        initialMetaRef.current = {
          businessRegNumber: (m.businessRegNumber as string) || '',
          gstNumber: (m.gstNumber as string) || '',
          yogaAllianceNumber: (m.yogaAllianceNumber as string) || '',
        }
        setDocData((prev) => ({
          ...prev,
          businessRegNumber: initialMetaRef.current!.businessRegNumber,
          gstNumber: initialMetaRef.current!.gstNumber,
          yogaAllianceNumber: initialMetaRef.current!.yogaAllianceNumber,
        }))
      } catch {
        if (mounted) toast.error('Could not load school verification details')
      } finally {
        if (mounted) setLoadingStatus(false)
      }
    }
    load()
    return () => {
      mounted = false
    }
  }, [schoolId])

  const handleDocFile = (key: keyof typeof docData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    const validation = validateDocumentFile(file, 'verificationDocument', 0)
    if (validation.valid) {
      setDocData((prev) => ({ ...prev, [key]: file }))
    } else {
      toast.error(validation.error || 'Invalid document')
    }
  }

  const handleDocText = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setDocData((prev) => ({ ...prev, [name]: value }))
  }

  const handleOtherDocuments = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return
    const acceptedTypes = [...ACCEPTED_MIME_TYPES.DOCUMENT] as readonly string[]
    const valid = Array.from(files).filter(
      (f) => acceptedTypes.includes(f.type) || (f.type === '' && /\.(pdf|jpe?g|png|webp)$/i.test(f.name))
    )
    e.target.value = ''
    if (valid.length === 0) {
      toast.error('Invalid file type. Please upload PDF or images (JPEG, PNG, WebP).')
      return
    }
    setOtherDocuments((prev) => [...prev, ...valid].slice(0, 5))
  }

  const removeOtherDocument = (index: number) => {
    setOtherDocuments((prev) => prev.filter((_, i) => i !== index))
  }

  const getAllDocuments = (): File[] => {
    const list: File[] = []
    if (docData.businessRegFile) list.push(docData.businessRegFile)
    if (docData.gstFile) list.push(docData.gstFile)
    if (docData.addressProofFile) list.push(docData.addressProofFile)
    if (docData.yogaAllianceFile) list.push(docData.yogaAllianceFile)
    return [...list, ...otherDocuments]
  }

  const getDocumentTypes = (): string[] => {
    const types: string[] = []
    if (docData.businessRegFile) types.push('businessReg')
    if (docData.gstFile) types.push('gst')
    if (docData.addressProofFile) types.push('addressProof')
    if (docData.yogaAllianceFile) types.push('yogaAlliance')
    otherDocuments.forEach(() => types.push('additional'))
    return types
  }

  const buildVerificationMeta = (): Record<string, string> => {
    const meta: Record<string, string> = {}
    if (docData.businessRegNumber.trim()) meta.businessRegNumber = docData.businessRegNumber.trim()
    if (docData.gstNumber.trim()) meta.gstNumber = docData.gstNumber.trim()
    if (docData.yogaAllianceNumber.trim()) meta.yogaAllianceNumber = docData.yogaAllianceNumber.trim()
    return meta
  }

  const hasMetaChanged = (): boolean => {
    const init = initialMetaRef.current
    if (!init) return false
    return (
      docData.businessRegNumber.trim() !== init.businessRegNumber.trim() ||
      docData.gstNumber.trim() !== init.gstNumber.trim() ||
      docData.yogaAllianceNumber.trim() !== init.yogaAllianceNumber.trim()
    )
  }

  const canSubmit = getAllDocuments().length > 0 || hasMetaChanged()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!schoolId) return

    const docs = getAllDocuments()
    if (docs.length === 0 && !hasMetaChanged()) {
      setErrors({ _general: 'Upload a document or change registration numbers to update.' })
      return
    }

    setIsLoading(true)
    setErrors({})
    try {
      if (docs.length === 0) {
        await schoolVerificationService.updateVerificationMeta(schoolId, {
          businessRegNumber: docData.businessRegNumber.trim(),
          gstNumber: docData.gstNumber.trim(),
          yogaAllianceNumber: docData.yogaAllianceNumber.trim(),
        })
        toast.success('Verification details updated on the school profile.')
        const refreshed = await schoolVerificationService.getStatus(schoolId)
        setExistingDocuments(refreshed.documents || [])
        setVerificationStatus(refreshed.status || 'PENDING')
        return
      }

      const res = await schoolVerificationService.submitDocuments(
        schoolId,
        docs,
        hasMetaChanged() ? buildVerificationMeta() : undefined,
        getDocumentTypes()
      )
      toast.success(res.message || 'Documents uploaded to school profile.')
      const refreshed = await schoolVerificationService.getStatus(schoolId)
      setExistingDocuments(refreshed.documents || [])
      setVerificationStatus(refreshed.status || 'PENDING')
      const m = refreshed.verificationMeta || {}
      initialMetaRef.current = {
        businessRegNumber: (m.businessRegNumber as string) || '',
        gstNumber: (m.gstNumber as string) || '',
        yogaAllianceNumber: (m.yogaAllianceNumber as string) || '',
      }
      setDocData({
        ...initialDocData,
        businessRegNumber: initialMetaRef.current.businessRegNumber,
        gstNumber: initialMetaRef.current.gstNumber,
        yogaAllianceNumber: initialMetaRef.current.yogaAllianceNumber,
      })
      setOtherDocuments([])
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err as Error)?.message ||
        'Failed to upload documents.'
      toast.error(msg)
      setErrors({ _general: msg })
    } finally {
      setIsLoading(false)
    }
  }

  if (!schoolId) {
    return (
      <div className="p-8 text-center text-gray-600">
        <p>Invalid school.</p>
        <button
          type="button"
          onClick={() => navigate('/admin/yoga-schools')}
          className="mt-4 text-amber-700 font-medium hover:underline"
        >
          Back to schools
        </button>
      </div>
    )
  }

  if (loadingStatus) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-amber-500 mx-auto" />
          <p className="mt-3 text-sm text-gray-600">Loading school verification details...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto py-4">
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center gap-4 mb-6"
      >
        <button
          type="button"
          onClick={() => navigate(`/admin/yoga-schools/${schoolId}`)}
          className="p-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-amber-500/20"
        >
          <ArrowLeft className="h-5 w-5 text-gray-600" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-gray-900">Upload verification documents</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {schoolName ? (
              <>
                For <span className="font-medium text-gray-800">{schoolName}</span> — same flow as the yoga school
                dashboard. Files are appended to their profile.
              </>
            ) : (
              'Upload documents on behalf of this yoga school (emailed docs, etc.).'
            )}
          </p>
        </div>
      </motion.div>

      <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-gray-900">
        <p className="font-semibold text-amber-900">Admin upload</p>
        <p className="mt-1 text-gray-700 leading-relaxed">
          Use this when a school sends documents by email. Same storage as the school dashboard: GST, business
          registration, address proof, etc. each keep one file — uploading again replaces the old file (no duplicates).
        </p>
      </div>

      <motion.section
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6 bg-white rounded-2xl shadow-sm border border-gray-200 p-6"
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Shield className="w-5 h-5 text-amber-500" />
            Documents already on profile
          </h2>
          <span
            className={`inline-flex self-start px-2.5 py-1 rounded-lg text-xs font-semibold ${
              verificationStatus === 'VERIFIED'
                ? 'bg-green-100 text-green-800'
                : verificationStatus === 'REJECTED'
                  ? 'bg-red-100 text-red-800'
                  : 'bg-amber-100 text-amber-800'
            }`}
          >
            Verification: {verificationStatus}
          </span>
        </div>
        <VerificationDocumentsList
          documents={existingDocuments}
          emptyMessage="No verification documents on this school yet. Upload files below."
        />
      </motion.section>

      <motion.form
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        onSubmit={handleSubmit}
        className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden max-w-2xl"
      >
        <div className="p-6 sm:p-8 space-y-6">
          <h2 className="text-lg font-semibold text-gray-900">Add new documents</h2>
          <button
            type="button"
            onClick={() => setShowDocumentsModal(true)}
            className="text-sm text-amber-700 hover:text-amber-800 font-medium"
          >
            What documents are needed?
          </button>
          <DocumentsRequiredModal
            isOpen={showDocumentsModal}
            onClose={() => setShowDocumentsModal(false)}
            checklistIntro="Match the sections below when uploading on behalf of the school."
          />

          {errors._general && (
            <div className="rounded-xl bg-red-50 border border-red-200 p-3 text-sm text-red-700">{errors._general}</div>
          )}

          <fieldset className="border border-gray-200 rounded-xl p-4 space-y-3">
            <legend className="text-sm font-semibold text-gray-800 px-1">
              Business Registration <span className="text-red-500">*</span>
            </legend>
            <p className="text-xs text-gray-500 -mt-1">
              GST certificate can be used here with GSTIN as the registration number.
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Registration number</label>
              <input
                type="text"
                name="businessRegNumber"
                value={docData.businessRegNumber}
                onChange={handleDocText}
                placeholder="e.g. U74999MH2020PTC123456"
                className={textInputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Upload certificate (PDF or image)</label>
              <input type="file" accept={ACCEPT_STRINGS.DOCUMENT} onChange={handleDocFile('businessRegFile')} className={fileInputClass} />
              {replaceHint('businessReg')}
              {docData.businessRegFile && <p className="mt-1 text-sm text-green-600">{docData.businessRegFile.name}</p>}
            </div>
          </fieldset>

          <fieldset className="border border-gray-200 rounded-xl p-4 space-y-3">
            <legend className="text-sm font-semibold text-gray-800 px-1">
              GST Registration <span className="text-gray-500 font-normal">(if applicable)</span>
            </legend>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">GST number</label>
              <input
                type="text"
                name="gstNumber"
                value={docData.gstNumber}
                onChange={handleDocText}
                placeholder="e.g. 27AABCU9603R1ZM"
                className={textInputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Upload GST certificate</label>
              <input type="file" accept={ACCEPT_STRINGS.DOCUMENT} onChange={handleDocFile('gstFile')} className={fileInputClass} />
              {replaceHint('gst')}
              {docData.gstFile && <p className="mt-1 text-sm text-green-600">{docData.gstFile.name}</p>}
            </div>
          </fieldset>

          <fieldset className="border border-gray-200 rounded-xl p-4 space-y-3">
            <legend className="text-sm font-semibold text-gray-800 px-1">Address Proof</legend>
            <p className="text-xs text-gray-500 -mt-1">Utility bill, lease agreement, or property tax receipt</p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Upload document</label>
              <input type="file" accept={ACCEPT_STRINGS.DOCUMENT} onChange={handleDocFile('addressProofFile')} className={fileInputClass} />
              {replaceHint('addressProof')}
              {docData.addressProofFile && <p className="mt-1 text-sm text-green-600">{docData.addressProofFile.name}</p>}
            </div>
          </fieldset>

          <fieldset className="border border-gray-200 rounded-xl p-4 space-y-3">
            <legend className="text-sm font-semibold text-gray-800 px-1">
              Yoga Alliance Certification <span className="text-gray-500 font-normal">(if certified)</span>
            </legend>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">RYS registration number</label>
              <input
                type="text"
                name="yogaAllianceNumber"
                value={docData.yogaAllianceNumber}
                onChange={handleDocText}
                placeholder="e.g. RYS-200-12345"
                className={textInputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Upload certificate</label>
              <input type="file" accept={ACCEPT_STRINGS.DOCUMENT} onChange={handleDocFile('yogaAllianceFile')} className={fileInputClass} />
              {replaceHint('yogaAlliance')}
              {docData.yogaAllianceFile && <p className="mt-1 text-sm text-green-600">{docData.yogaAllianceFile.name}</p>}
            </div>
          </fieldset>

          <fieldset className="border border-gray-200 rounded-xl p-4 space-y-3">
            <legend className="text-sm font-semibold text-gray-800 px-1">
              Additional Documents <span className="text-gray-500 font-normal">(optional)</span>
            </legend>
            <input type="file" accept={ACCEPT_STRINGS.DOCUMENT} multiple onChange={handleOtherDocuments} className={fileInputClass} />
            {otherDocuments.length > 0 && (
              <ul className="space-y-1.5">
                {otherDocuments.map((file, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                    <FileText className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                    <span className="truncate flex-1">{file.name}</span>
                    <button type="button" onClick={() => removeOtherDocument(i)} className="text-red-500 hover:text-red-600 shrink-0">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </fieldset>
        </div>

        <div className="px-6 sm:px-8 py-4 bg-gray-50 border-t border-gray-100 flex gap-2">
          <button
            type="button"
            onClick={() => navigate(`/admin/yoga-schools/${schoolId}`)}
            className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading || !canSubmit}
            className="flex-1 flex justify-center items-center gap-1.5 py-2.5 px-4 text-sm font-medium text-white bg-amber-500 rounded-xl hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <CheckCircle className="h-4 w-4" />
                Upload to school profile
              </>
            )}
          </button>
        </div>
      </motion.form>
    </div>
  )
}

export default UploadSchoolDocuments
